import React, { useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import type { ThemeOption } from '../contexts/ThemeContext';
import { Button, Card, Typography } from './ui';

const ThemeSelector: React.FC = () => {
  const { theme, setTheme, themes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const currentTheme = themes.find((t: ThemeOption) => t.name === theme);

  return (
    <div className="relative inline-block">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-1 h-8"
        title={`Current theme: ${currentTheme?.label}`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"
          />
        </svg>
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <Card
            className="absolute top-full mt-2 right-0 z-20 backdrop-blur-sm max-h-80 overflow-y-auto min-w-max"
            variant="default"
            padding="sm"
            shadow="lg"
            rounded="lg"
          >
            <div className="space-y-2">
              {themes.map((themeOption: ThemeOption) => (
                <div
                  key={themeOption.name}
                  onClick={() => {
                    setTheme(themeOption.name);
                    setIsOpen(false);
                  }}
                  className={`
                    flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors duration-150
                    ${theme === themeOption.name ? 'text-white' : ''}
                  `}
                  style={{
                    backgroundColor:
                      theme === themeOption.name
                        ? 'var(--accent)'
                        : 'transparent',
                    color:
                      theme === themeOption.name
                        ? 'white'
                        : 'var(--text-primary)',
                  }}
                  onMouseEnter={e => {
                    if (theme !== themeOption.name) {
                      e.currentTarget.style.backgroundColor =
                        'var(--bg-secondary)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (theme !== themeOption.name) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex gap-1">
                    <div
                      className="w-3 h-3 rounded-full border"
                      style={{
                        backgroundColor: themeOption.preview.primary,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <div
                      className="w-3 h-3 rounded-full border"
                      style={{
                        backgroundColor: themeOption.preview.secondary,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <div
                      className="w-3 h-3 rounded-full border"
                      style={{
                        backgroundColor: themeOption.preview.accent,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </div>
                  <Typography variant="body2" className="flex-1">
                    {themeOption.label}
                  </Typography>
                  {theme === themeOption.name && (
                    <svg
                      className="w-4 h-4"
                      style={{ color: 'white' }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ThemeSelector;
